/**
 * The bytes behind a chain of title: signing an entry's documents so they can
 * be looked at, and uploading a draft's documents once the entry exists.
 *
 * One of two paths in this app that go to Supabase Storage directly rather
 * than through the ONDO API — `workImages.ts` is the other — and for the same
 * reason: bytes, not policy. The client carries the caller's own JWT, so the
 * bucket's policies decide exactly as they would behind a proxy, and a 25MB
 * scan is not buffered twice. Which documents a click means is
 * `provenance.model.ts`'s question; when to sign them is the spotlight hook's.
 *
 * A path missing from a signed map is a path storage refused. That is a state
 * a reader can be in — reaching an entry without reaching its evidence — and
 * never a fault.
 */

import type { ProvenanceDocumentKind } from "@ondo/shared";

import { ApiError } from "./api.ts";
import { supabase } from "./supabase.ts";

/** The bucket the storage policies in 20260804090500 are written against. */
const BUCKET = "provenance";

/**
 * How long a signed document URL stays good for.
 *
 * The same window `workImages.ts` uses, and for the same reason: long enough to
 * open a thing and read it, short enough that a URL lifted out of the DOM is
 * not a share. `provenance_documents_download` authorises off
 * `readable_provenance_entry_ids()`, so the bytes follow the entry — and a
 * signed URL is the one way a reader can hold one without that policy running
 * again.
 */
const DOCUMENT_URL_TTL_SECONDS = 600;

/**
 * Signs a whole entry's documents in one round trip.
 *
 * Called when somebody asks to look, not when a card is expanded. Expanding a
 * link with twelve invoices attached should cost nothing until one of them is
 * wanted — the same argument this screen already makes for fetching
 * confirmations lazily — and signing at the moment of use means the URL is
 * seconds old rather than having spent its ten minutes sitting in a panel.
 *
 * Paths missing from the returned map are paths storage refused. That is a
 * state and not a fault: a reader may reach an entry through a route that does
 * not reach its evidence.
 */
export async function signProvenanceDocuments(
  paths: string[],
): Promise<Map<string, string>> {
  if (paths.length === 0) return new Map();

  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrls(paths, DOCUMENT_URL_TTL_SECONDS);

  if (error || !data) return new Map();

  return new Map(
    data.flatMap((row) =>
      row.path === null || row.signedUrl === null ? [] : [[row.path, row.signedUrl] as const],
    ),
  );
}

export interface UploadableDocument {
  /** The provenance entry the document is evidence for. A server id. */
  entryId: string;
  file: File;
  kind: ProvenanceDocumentKind;
  /** What it is, in the uploader's words. Falls back to the file's own name. */
  label?: string | null;
  /**
   * Where it sits among the entry's documents.
   *
   * Supplied by the caller when it uploads one file at a time — which the
   * ledgered writer does, so a mid-batch failure re-uploads only what failed.
   * Without it every such call would file `position 0`.
   */
  position?: number;
}

/**
 * Attaches supporting evidence to provenance entries.
 *
 * The second data path in this app that does not go through the ONDO API, and
 * the reason is the one `registrationImages.ts` gives: bytes rather than policy.
 * A 25MB sale agreement proxied through Nest is buffered twice for no gain, and
 * the authorisation is byte-for-byte identical either way — this client carries
 * the user's own JWT, so `provenance_documents_insert` and the bucket's
 * `provenance_documents_upload` policy decide.
 *
 * It is also forced. `object_path` is a GENERATED column —
 * 'prov/<entry_id>/<document_id>' — so the destination is only knowable once the
 * database has minted the id, and nothing here proposes a path. That is the
 * property the storage policies depend on.
 *
 * Sequential, not `Promise.all`, so a failure names one file.
 */
export async function uploadProvenanceDocuments(
  documents: UploadableDocument[],
): Promise<void> {
  const db = supabase.schema("ondo");
  const positions = new Map<string, number>();

  for (const document of documents) {
    // Per entry, not per call: two entries each carrying their first document
    // both want position 0.
    const position = document.position ?? positions.get(document.entryId) ?? 0;
    positions.set(document.entryId, position + 1);

    const { data, error } = await db
      .from("provenance_documents")
      .insert({
        entry_id: document.entryId,
        kind: document.kind,
        label: document.label ?? document.file.name,
        content_type: document.file.type,
        byte_size: document.file.size,
        position,
      })
      .select("id, object_path")
      .single();

    if (error) {
      throw new ApiError(403, `Could not attach ${document.file.name}: ${error.message}`);
    }
    if (!data.object_path) {
      throw new ApiError(
        500,
        `Could not attach ${document.file.name}: no object path was returned.`,
      );
    }

    const upload = await supabase.storage
      .from(BUCKET)
      .upload(data.object_path, document.file, { contentType: document.file.type });

    if (upload.error) {
      // A row pointing at bytes that were never written would show a reviewer —
      // or a verifier who was asked to look at exactly this — a document that
      // 404s. Drop it and surface the failure.
      await db.from("provenance_documents").delete().eq("id", data.id);
      throw new ApiError(
        502,
        `Could not upload ${document.file.name}: ${upload.error.message}`,
      );
    }
  }
}
